import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';

// Pages
import LoginPage from './pages/auth/LoginPage'
import DashboardPage from './pages/DashboardPage'
// Lazy imports
import UsersPage from './pages/users/UsersPage'
import RolesPage from './pages/roles/RolesPage'
import MenusPage from './pages/menus/MenusPage'
import CrudsPage from './pages/cruds/CrudsPage'
import CrudDataPage from './pages/cruds/CrudDataPage'
import AIAssistantPage from './pages/ai/AiAssistantPage'
import SettingsPage from './pages/settings/SettingsPage'
import NotFoundPage from './pages/NotFoundPage'
// Pacientes
import PacientesPage from './pages/pacientes/PacientesPage'
import PacienteFormPage from './pages/pacientes/PacienteFormPage'
import ConsultaPage from './pages/pacientes/ConsultaPage'
import HistorialClinicoPage from './pages/pacientes/HistorialClinicoPage'
import RecetaPage from './pages/pacientes/RecetaPage'
import ConsultaEditPage from './pages/pacientes/ConsultaEditPage'
// Medico VFP
import MedicoVfpPage from './pages/medico-vfp/MedicoVfpPage';
import MedicoVfpCreatePage from './pages/medico-vfp/MedicoVfpCreatePage';
import MedicoVfpHistorialPage from './pages/medico-vfp/MedicoVfpHistorialPage';
import MedicoVfpEditPage from './pages/medico-vfp/MedicoVfpEditPage';
import MedicoVfpRecetaPage from './pages/medico-vfp/MedicoVfpRecetaPage';
import MedicoVfpRecetaPrintPage from './pages/medico-vfp/MedicoVfpRecetaPrintPage';
import MedicoVfpOrdenPrintPage from './pages/medico-vfp/MedicoVfpOrdenPrintPage';
import RecetaRapidaPage from './pages/medico-vfp/RecetaRapidaPage';
import RecetaRapidaPrintPage from './pages/medico-vfp/RecetaRapidaPrintPage';
import RecetaPublicPage from './pages/public/RecetaPublicPage';
// Cirugías
import CirugiasPage from './pages/cirugias/CirugiasPage';
// Reportes
import ReporteConsultaHospitalizacionPage from './pages/reportes/ReporteConsultaHospitalizacionPage';

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/receta/:token" element={<RecetaPublicPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        
        <Route
          path="/users" 
          element={
            <ProtectedRoute requiredPermission="users.read">
              <UsersPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/roles" 
          element={
            <ProtectedRoute requiredPermission="roles.read">
              <RolesPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/menus" 
          element={
            <ProtectedRoute requiredPermission="menus.read">
              <MenusPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/cruds" 
          element={
            <ProtectedRoute requiredPermission="cruds.read">
              <CrudsPage />
            </ProtectedRoute>
          } 
        /><Route 
          path="/cruds/:id/data" 
          element={
            <ProtectedRoute requiredPermission="cruds.read">
              <CrudDataPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/ai-assistant" 
          element={
            <ProtectedRoute requiredPermission="ai.access">
              <AIAssistantPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute requiredPermission="settings.read">
              <SettingsPage />
            </ProtectedRoute>
          } 
        />

        {/* Pacientes Routes */}
        <Route path="/pacientes" element={<PacientesPage />} />
        <Route path="/pacientes/nuevo" element={<PacienteFormPage />} />
        <Route path="/pacientes/:id/editar" element={<PacienteFormPage />} />
        <Route path="/pacientes/:id/consulta" element={<ConsultaPage />} />
        <Route path="/pacientes/:id/historial" element={<HistorialClinicoPage />} />
        <Route path="/pacientes/consultas/:consultaId/receta" element={<RecetaPage />} />
        <Route path="/pacientes/consultas/:consultaId/editar" element={<ConsultaEditPage />} />

        {/* Medico VFP Routes */}
        <Route path="/medico-vfp/pacientes" element={<MedicoVfpPage />} />
        <Route path="/medico-vfp/nuevo" element={<MedicoVfpCreatePage />} />
        <Route path="/medico-vfp/:clave/historial" element={<MedicoVfpHistorialPage />} />
        <Route path="/medico-vfp/:clave/receta" element={<MedicoVfpRecetaPage />} />
        <Route path="/medico-vfp/:clave/receta/print" element={<MedicoVfpRecetaPrintPage />} />
        <Route path="/medico-vfp/:clave/orden/print" element={<MedicoVfpOrdenPrintPage />} />
        <Route path="/medico-vfp/:clave/editar" element={<MedicoVfpEditPage />} />

        {/* Receta Rápida */}
        <Route path="/receta-rapida" element={<RecetaRapidaPage />} />
        <Route path="/receta-rapida/print" element={<RecetaRapidaPrintPage />} />

        {/* Cirugías Routes */}
        <Route path="/cirugias" element={<CirugiasPage />} />

        {/* Reportes Routes */}
        <Route path="/reportes/consulta-hospitalizacion" element={<ReporteConsultaHospitalizacionPage />} />

        <Route path="/receta/:token" element={<RecetaPublicPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Layout>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App
